import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import Icon from "Components/Icon/Icon";
import COPY from "./copy";
import styles from "./FeatureBloqueada.module.scss";

/**
 * Estado "esta feature no esta en tu plan" (Figma 3352-22215): un solo componente
 * para todas las features; lo que cambia es el copy (./copy.js).
 *
 * La variante sale del copy y no de un prop, para que no se desincronice del
 * diseno: con `benefits` es la grande (3352:27222), sin `benefits` la compacta
 * (3352:26749). El color sale del plan que se vende, no de la variante.
 * `inline` es para el tab de ARCA, donde el bloqueo ES la pantalla (3352:27408).
 *
 * Una key sin copy devuelve null: mejor nada que un modal vacio tapando una accion.
 */

function CloseButton({ onClose }) {
  const { t } = useTranslation();
  if (!onClose) return null;
  return (
    <button type="button" className={styles.close} onClick={onClose} aria-label={t("close", "Cerrar")}>
      <Icon name="close_small" size={24} />
    </button>
  );
}

export function FeatureBloqueadaContent({ featureKey, values = {}, onVerPlanes, onClose, inline }) {
  const { t } = useTranslation();
  const copy = COPY[featureKey];
  if (!copy) return null;

  const [titleKey, titleDefault] = copy.title;
  const [availabilityKey, availabilityDefault] = copy.availability;
  // Un copy con {{limit}} trae una version sin numero para cuando el tope no llego:
  // mejor eso que "hasta  profesionales".
  const [descKey, descDefault] =
    copy.descriptionWithoutLimit && values.limit == null ? copy.descriptionWithoutLimit : copy.description;
  const theme = copy.plan === "Premium" ? styles.premium : styles.pro;
  const titleId = `feature-bloqueada-${featureKey}`;

  const title = (
    <h2 id={titleId} className={styles.title}>
      {t(titleKey, titleDefault)}
    </h2>
  );
  const pill = (
    <span className={styles.pill}>
      {t(availabilityKey, availabilityDefault)}
    </span>
  );
  const cta = (
    <button type="button" className={styles.cta} onClick={onVerPlanes}>
      {t("plan_locked_see_plans", "Ver planes")}
    </button>
  );

  const benefits = copy.benefits && (
    <ul className={styles.benefits}>
      {copy.benefits.map(([key, value]) => (
        <li key={key}>
          <Icon name="check" size={16} className={styles.check} />
          <span>{t(key, value)}</span>
        </li>
      ))}
    </ul>
  );

  // Inline (3352:27408, tab de ARCA): ocupa la pantalla, no interrumpe una accion.
  // Circulo sin candado, sin X, y el pill arriba del CTA (pedido de QA en la card
  // 854, 2026-10-06: el Figma lo tenia debajo).
  if (inline) {
    return (
      <div data-testid="feature-bloqueada" className={`${styles.inlineLayout} ${theme}`}>
        <span data-testid="feature-bloqueada-icon" className={styles.inlineIcon}>
          <Icon name={copy.icon} size={24} />
        </span>
        {title}
        <div className={styles.inlineBody}>
          <p className={styles.description}>{t(descKey, descDefault, values)}</p>
          {benefits}
        </div>
        {pill}
        {cta}
      </div>
    );
  }

  if (!copy.benefits) {
    return (
      <div data-testid="feature-bloqueada" className={`${styles.compact} ${theme}`}>
        <div className={styles.header}>
          <CloseButton onClose={onClose} />
        </div>
        <div className={styles.hero}>
          <span data-testid="feature-bloqueada-icon" className={styles.iconBadge}>
            <Icon name={copy.icon} size={24} />
            <span data-testid="feature-bloqueada-lock" className={styles.lockBadge}>
              <Icon name="lock" size={12} />
            </span>
          </span>
          {title}
        </div>
        <p className={styles.description}>{t(descKey, descDefault, values)}</p>
        {pill}
        {cta}
      </div>
    );
  }

  return (
    <div data-testid="feature-bloqueada" className={`${styles.large} ${theme}`}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span data-testid="feature-bloqueada-icon" className={styles.titleIcon}>
            <Icon name={copy.icon} size={16} />
          </span>
          {title}
        </div>
        <CloseButton onClose={onClose} />
      </div>
      <p className={styles.description}>{t(descKey, descDefault, values)}</p>
      {benefits}
      {pill}
      {cta}
    </div>
  );
}

export default function FeatureBloqueada({ featureKey, values, show, onClose, onVerPlanes, inline }) {
  const copy = COPY[featureKey];
  if (!copy) return null;

  // TODO(item 5 de la card 854): el destino real es la seccion Planes de Setup,
  // que todavia no existe. Hasta entonces el CTA cierra el modal; el consumidor
  // puede pasar `onVerPlanes` para sobreescribirlo.
  const verPlanes = onVerPlanes || onClose;

  if (inline) {
    return (
      <div className={styles.inline}>
        <FeatureBloqueadaContent featureKey={featureKey} values={values} onVerPlanes={verPlanes} inline />
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      aria-labelledby={`feature-bloqueada-${featureKey}`}
      dialogClassName={copy.benefits ? styles.dialogLarge : styles.dialogCompact}
      contentClassName={styles.modalContent}
    >
      <Modal.Body className="p-4">
        <FeatureBloqueadaContent featureKey={featureKey} values={values} onVerPlanes={verPlanes} onClose={onClose} />
      </Modal.Body>
    </Modal>
  );
}
